import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Badge, Card, List, Space, Tag, Typography } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAuth } from '../App';
import { EmptyState } from '../components/EmptyState';
import { chatAPI } from '../utils/api';

const { Title, Text } = Typography;

interface ConversationRow {
  id: number;
  patient_id: number;
  doctor_id: number;
  updated_at: string;
  patient_full_name?: string | null;
  week_of_pregnancy?: number | null;
  last_message_preview?: string | null;
  unread_count?: number;
  is_support?: boolean;
}

export const DoctorsChatsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<ConversationRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.role === 'doctor') {
      void loadConversations();
    }
  }, [user]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      const response = await chatAPI.getConversations();
      setConversations((response.data || []) as ConversationRow[]);
    } catch (error) {
      console.error('Error loading doctor conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ color: '#263238', marginBottom: 8 }}>Чаты врача</Title>
        <Text type="secondary">Список чатов с именем пациентки, сроком беременности и превью последнего сообщения.</Text>
      </div>

      <Card style={{ borderRadius: 18 }} loading={loading}>
        {conversations.length === 0 && !loading ? (
          <EmptyState description="У врача пока нет чатов" />
        ) : (
          <List
            dataSource={conversations}
            renderItem={(conv) => (
              <List.Item
                onClick={() => navigate(`/chat/${conv.id}`)}
                style={{ cursor: 'pointer', padding: '16px 8px' }}
              >
                <List.Item.Meta
                  avatar={
                    <Badge count={conv.unread_count || 0} offset={[-4, 4]}>
                      <Avatar icon={<UserOutlined />} size="large" style={{ backgroundColor: '#7CB342' }} />
                    </Badge>
                  }
                  title={
                    <Space wrap>
                      <Text strong>{conv.patient_full_name || `Пациентка #${conv.patient_id}`}</Text>
                      {conv.week_of_pregnancy ? <Tag color="blue">{conv.week_of_pregnancy} нед.</Tag> : null}
                      {conv.unread_count ? <Tag color="green">{conv.unread_count} непрочит.</Tag> : null}
                    </Space>
                  }
                  description={
                    <Space direction="vertical" size={4}>
                      <Text type="secondary">{conv.last_message_preview || 'Сообщений пока нет'}</Text>
                      <Text type="secondary">Обновлено {dayjs(conv.updated_at).format('DD.MM.YYYY HH:mm')}</Text>
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Card>
    </div>
  );
};
